import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import * as yup from 'yup';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {history} from 'core/history';

import {ROUTE} from 'core/routes';
import {doLogout, profile$} from 'models/auth';
import {changePasswordFx, state$} from 'models/password';

import {colors} from 'theme';

import {getPasswordSchema} from './helpers/passwordSchema';
import {PasswordValidationRules} from './helpers/passwordValidationRules';

import {
    ButtonContainer,
    Field,
    Fields,
    Form,
    FormDescription,
    MainContainer,
    PasswordContent,
    PasswordTitle,
} from './styled';

type ChangePasswordForm = {currentPassword: string; password: string; passwordConfirm: string};

export function ChangePasswordPage() {
    const {t, i18n} = useTranslation();
    const state = useStore(state$);
    const profile = useStore(profile$);

    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) =>
            t(`pages.password.validations.${key}`, replace),
        [i18n.language],
    );

    const schema = useMemo(
        () =>
            yup.object().shape({
                currentPassword: yup.string().required(translateValidates('required')),
                password: getPasswordSchema(translateValidates),
                passwordConfirm: yup
                    .string()
                    .required(translateValidates('required'))
                    .oneOf([yup.ref('password')], translateValidates('confirm_password_mismatch')),
            }),
        [translateValidates],
    );

    const {control, handleSubmit, formState, watch, errors} = useForm<ChangePasswordForm>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {
            currentPassword: '',
            password: '',
            passwordConfirm: '',
        },
    });

    const watchPassword = watch('password', '');

    useEffect(() => {
        if (state.success) {
            history.push('/login');
        }
    }, [state.success]);

    const onSubmit = async (data: ChangePasswordForm) => {
        if (!profile?.email) return;
        try {
            await changePasswordFx({
                currentPassword: data.currentPassword,
                newPassword: data.password,
                confirmation: data.passwordConfirm,
                userName: profile.email,
                email: profile.email,
            });
            doLogout(true);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <MainContainer>
            <PasswordContent style={{background: colors.background, padding: 0}}>
                <PasswordTitle size={3}>{t('pages.password.change.title')}</PasswordTitle>
                <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                    <FormDescription>{t('pages.password.change.description')}</FormDescription>
                    <Fields>
                        {/* Current Password */}
                        <Field
                            isError={!!errors.currentPassword}
                            text={errors.currentPassword?.message}
                        >
                            <span>{t('pages.password.change.fields.currentPassword.label')}</span>
                            <Controller
                                name="currentPassword"
                                control={control}
                                render={({onChange, value, name}) => (
                                    <Input
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        type="password"
                                        placeholder={t(
                                            'pages.password.change.fields.currentPassword.placeholder',
                                        )}
                                        disabled={state.pending}
                                        hasError={!!errors.currentPassword}
                                    />
                                )}
                            />
                        </Field>

                        {/* New Password */}
                        <Field isError={!!errors.password}>
                            <span>{t('pages.password.change.fields.newPassword.label')}</span>
                            <Controller
                                name="password"
                                control={control}
                                render={({onChange, value, name}) => (
                                    <Input
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        type="password"
                                        placeholder={t(
                                            'pages.password.change.fields.newPassword.placeholder',
                                        )}
                                        autoComplete="new-password"
                                        disabled={state.pending}
                                        hasError={!!errors.password}
                                        isValid={formState.dirtyFields.password && !errors.password}
                                    />
                                )}
                            />
                            <PasswordValidationRules watchValue={watchPassword} />
                        </Field>

                        {/* Confirm New Password */}
                        <Field
                            isError={!!errors.passwordConfirm}
                            text={errors.passwordConfirm?.message}
                        >
                            <span>
                                {t('pages.password.change.fields.confirmNewPassword.label')}
                            </span>
                            <Controller
                                name="passwordConfirm"
                                control={control}
                                render={({onChange, value, name}) => (
                                    <Input
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        type="password"
                                        placeholder={t(
                                            'pages.password.change.fields.confirmNewPassword.placeholder',
                                        )}
                                        autoComplete="new-password"
                                        disabled={state.pending}
                                        hasError={!!errors.passwordConfirm}
                                        isValid={
                                            formState.dirtyFields.passwordConfirm &&
                                            !errors.passwordConfirm
                                        }
                                    />
                                )}
                            />
                        </Field>
                    </Fields>

                    <ButtonContainer>
                        <Button
                            type="submit"
                            isLoading={state.pending}
                            disabled={!formState.isValid}
                            data-id="btn-submit"
                        >
                            {t('pages.password.change.submitButton')}
                        </Button>
                        <Button
                            variant={ButtonVariant.Text}
                            onClick={() => history.push(ROUTE.PROFILE)}
                        >
                            {t('pages.password.change.returnButton')}
                        </Button>
                    </ButtonContainer>
                </Form>
            </PasswordContent>
        </MainContainer>
    );
}
